// src/api/useApi.ts

import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import axiosClient from './axios-client';

/**
 * Interface représentant une erreur d'API.
 */
export interface ApiError {
    message: string;
    isAuthError?: boolean;
    response?: {
        data: {
            message: string;
            errors: Record<string, string[]>;
        };
    };
}

/**
 * Interface étendue pour les options de mutation, incluant les callbacks onSuccess et onError.
 */
export interface MutationOptions<TData, TError, TVariables> extends UseMutationOptions<TData, TError, TVariables> {
    onSuccess?: (data: TData, variables?: TVariables, context?: any) => void;
    onError?: (error: TError, variables?: TVariables, context?: any) => void;
}

/**
 * Type représentant la réponse d'une API, incluant les données, les liens et les métadonnées de pagination.
 */
export type ApiResponse<T> = {
    data: T;
    links?: {
        first: string | null;
        last: string | null;
        next: string | null;
        prev: string | null;
    };
    meta?: {
        current_page: number;
        from: number;
        last_page: number;
        links: Array<{
            active: boolean;
            label: string;
            url: string;
        }>;
        path: string;
        per_page: number;
        to: number;
        total: number;
    };
};

/**
 * Fonction utilitaire pour déterminer si une erreur est une erreur d'authentification.
 * @param error Erreur à évaluer.
 * @returns Booléen indiquant si l'erreur est une erreur d'authentification.
 */
const isAuthError = (error: unknown): boolean => {
    return axios.isAxiosError(error) && (error as AxiosError).response?.status === 401;
};

/**
 * Fonction utilitaire pour vérifier si une valeur est un fichier (File ou Blob).
 * @param value Valeur à vérifier.
 * @returns Booléen indiquant si la valeur est un fichier.
 */
const isFile = (value: any): value is File | Blob => {
    return typeof value === 'object' && value !== null && (value instanceof File || value instanceof Blob);
};

/**
 * Wrapper autour de `useQuery` de React Query avec gestion des erreurs d'authentification.
 * @param url URL de la requête.
 * @param config Configuration Axios pour la requête.
 * @param options Options supplémentaires pour useQuery.
 * @returns Résultat de useQuery.
 */
export function useApiQuery<TData = unknown>(
    url: string,
    config: AxiosRequestConfig = {},
    options?: UseQueryOptions<TData, ApiError>
) {
    const isEnabled = url !== ''; // Détermine si la requête doit être activée.

    return useQuery<TData, ApiError>(
        [url, config], // Clé de la requête.
        () => axiosClient.get(url, config).then((res) => res.data), // Fonction de récupération des données.
        {
            ...options,
            enabled: isEnabled && options?.enabled !== false, // Combine les conditions d'activation.
            onError: (error: ApiError) => {
                if (isAuthError(error)) {
                    console.error('Authentication error, redirecting...');
                    // Ici, gérez l'erreur d'authentification globalement (ex. redirection vers la page de connexion).
                }
                // Appel au callback onError défini par l'utilisateur, s'il existe.
                options?.onError?.(error);
            },
        }
    );
}

/**
 * Wrapper autour de `useMutation` de React Query avec gestion des erreurs d'authentification.
 * @param method Méthode HTTP à utiliser ('post', 'put', 'patch', 'delete').
 * @param getUrl Fonction pour obtenir l'URL en fonction des variables.
 * @param options Options supplémentaires pour useMutation.
 * @returns Résultat de useMutation.
 */
// src/api/useApi.ts (ou le fichier approprié)

export function useApiMutation<
    TData = unknown,
    TError = ApiError,
    TVariables = any
>(
    method: 'post' | 'put' | 'patch' | 'delete',
    getUrl: (variables: TVariables) => string,
    options?: UseMutationOptions<TData, TError, TVariables>
) {
    const mutationFn = (variables: TVariables) => {
        const url = getUrl(variables);

        let data: any = variables;

        // Vérifier si les variables contiennent au moins un fichier
        const hasFile = variables && typeof variables === 'object' && !Array.isArray(variables)
            ? Object.values(variables).some(isFile)
            : false;

        if (hasFile) {
            data = new FormData();
            for (const key in variables) {
                const value = variables[key];
                if (value !== undefined && value !== null) {
                    if (isFile(value)) {
                        data.append(key, value);
                    } else if (typeof value === 'object') {
                        // Si la valeur est un objet non-fichier, la sérialiser
                        data.append(key, JSON.stringify(value));
                    } else {
                        // Pour les chaînes de caractères ou autres types primitifs
                        data.append(key, String(value));
                    }
                }
            }
        }

        return axiosClient.request({
            url,
            method,
            data,
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
        }).then((res) => res.data);
    };

    return useMutation<TData, TError, TVariables>(mutationFn, {
        ...options,
        onError: (error: TError, variables: TVariables, context: any) => {
            if (isAuthError(error)) {
                console.error('Authentication error, handling...');
                // Gestion spécifique de l'erreur d'authentification (ex. redirection vers la page de login).
            }
            // Appel au callback onError défini par l'utilisateur, s'il existe.
            options?.onError?.(error, variables, context);
        },
    });
}

